import {
  AutomationFailureResponseStatus,
  type AutomationFailureResponse,
} from '@urbanx/agx.support.client';
import {
  AgxBadge,
  AgxButton,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  type IColumnConfig,
} from '@urbanx/agx.ui-components2';
import {
  formatAutomationContextName,
  formatAutomationContextValue,
  formatAutomationFailureRelativeInstant,
  formatAutomationFailureShortInstant,
  getAutomationAreaLabel,
  getAutomationReasonLabel,
  getAutomationResolutionLabel,
} from './automationFailurePresentation';

export interface AutomationErrorsTableProps {
  failures: AutomationFailureResponse[];
  isPending: boolean;
  onSelectFailure: (failure: AutomationFailureResponse) => void;
  showCampaign?: boolean;
  emptyLabel?: string;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}

const contextAttributes = (failure: AutomationFailureResponse) => {
  const attributes = (failure.attributes ?? []).filter(
    (attribute) => attribute.name && attribute.value
  );

  return [
    ...new Map(
      attributes.map((attribute) => [`${attribute.name}:${attribute.value}`, attribute])
    ).values(),
  ];
};

const ContextCell = ({ failure }: { failure: AutomationFailureResponse }) => {
  const attributes = contextAttributes(failure);

  if (attributes.length === 0) {
    return (
      <AgxTypography
        id={`automation-error-${failure.reference}-context`}
        type="body_ui"
        size="small"
      >
        {failure.taskId ? 'Task' : failure.contractId ? 'Contract' : 'Campaign level'}
      </AgxTypography>
    );
  }

  return (
    <AgxSpacer direction="vertical" gap="2xs">
      {attributes.map((attribute) => (
        <AgxTypography
          key={`${attribute.name}:${attribute.value}`}
          id={`automation-error-${failure.reference}-${attribute.name}`}
          type="body_ui"
          size="small"
        >
          {`${formatAutomationContextName(attribute.name)}: ${formatAutomationContextValue(attribute)}`}
        </AgxTypography>
      ))}
    </AgxSpacer>
  );
};

export const AutomationErrorsTable = ({
  failures,
  isPending,
  onSelectFailure,
  showCampaign = true,
  emptyLabel = 'No automation errors match these filters.',
  hasMore,
  onLoadMore,
  isLoadingMore,
}: AutomationErrorsTableProps) => {
  const columns: IColumnConfig[] = [
    { name: 'Problem', maxColWidth: 340, shrink: true },
    ...(showCampaign ? [{ name: 'Campaign', maxColWidth: 320, shrink: true }] : []),
    { name: 'Affected', maxColWidth: 260, shrink: true },
    { name: 'Resolution', shrink: true },
    { name: 'When', align: 'right', shrink: true },
  ];

  const rows = failures.map((failure, index) => {
    const id = failure.reference ?? index.toString();
    const isResolved = failure.status === AutomationFailureResponseStatus.Resolved;
    const resolution = isResolved ? failure.resolutionHistory?.at(-1)?.type : undefined;

    return {
      dataTestId: `automation-error-${id}`,
      Problem: (
        <AgxSpacer direction="vertical" gap="2xs">
          <AgxTypography id={`automation-error-${id}-name`} type="body_ui_strong" size="small">
            {failure.automationName} failed
          </AgxTypography>
          <AgxTypography
            id={`automation-error-${id}-summary`}
            type="body_ui"
            size="small"
            color="var(--text-tertiary)"
          >
            {[
              failure.area && getAutomationAreaLabel(failure.area),
              failure.reason && getAutomationReasonLabel(failure.reason),
            ]
              .filter(Boolean)
              .join(' · ')}
          </AgxTypography>
        </AgxSpacer>
      ),
      ...(showCampaign
        ? {
            Campaign: (
              <AgxSpacer direction="vertical" gap="2xs">
                <AgxTypography
                  id={`automation-error-${id}-campaign`}
                  type="body_ui_strong"
                  size="small"
                >
                  {failure.campaignAddress ?? failure.campaignId}
                </AgxTypography>
                {failure.agencyName && (
                  <AgxTypography
                    id={`automation-error-${id}-agency`}
                    type="caption"
                    size="small"
                    color="var(--text-secondary)"
                  >
                    {failure.agencyName}
                  </AgxTypography>
                )}
              </AgxSpacer>
            ),
          }
        : {}),
      Affected: <ContextCell failure={failure} />,
      Resolution: (
        <AgxBadge
          type="colourfill"
          size="md"
          colour={isResolved ? 'lightgrey' : 'red'}
          text={isResolved ? getAutomationResolutionLabel(resolution) : 'Unresolved'}
        />
      ),
      When: failure.occurredUtc ? (
        <AgxSpacer direction="vertical" gap="3xs" contentAlignment="end">
          <AgxTypography id={`automation-error-${id}-occurred`} type="body_ui" size="small" noWrap>
            {formatAutomationFailureRelativeInstant(failure.occurredUtc)}
          </AgxTypography>
          <AgxTypography
            id={`automation-error-${id}-occurred-at`}
            type="body_ui"
            size="small"
            color="var(--text-tertiary)"
            noWrap
          >
            {formatAutomationFailureShortInstant(failure.occurredUtc)}
          </AgxTypography>
        </AgxSpacer>
      ) : undefined,
    };
  });

  return (
    <AgxSpacer direction="vertical" gap="sm" fill>
      <AgxTable
        columns={columns}
        rows={rows}
        variant={rows.length ? 'default' : 'minimal'}
        tableStyle="square"
        showLoadingState={isPending && rows.length === 0}
        noDataLabel={emptyLabel}
        noDataGlyphType="shield"
        onRowClick={(rowIndex) => {
          const failure = failures[rowIndex];
          if (failure) onSelectFailure(failure);
        }}
      />
      {hasMore && (
        <AgxSpacer direction="horizontal" contentJustification="center">
          <AgxButton
            id="automation-errors-load-more"
            text={isLoadingMore ? 'Loading…' : 'Load more'}
            variant="hollow"
            size="medium"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          />
        </AgxSpacer>
      )}
    </AgxSpacer>
  );
};
